import { useTheme } from '@emotion/react'
import loadable from '@loadable/component'
import {
    AppState,
    CommentEvent,
    ConfigurationContext,
    EntitledToView,
    Product,
    useFeature,
} from '@news-mono/web-common'
import React, { useContext } from 'react'
import { useSelector } from 'react-redux'
import { MessageBanner } from '../banners/MessageBanner/MessageBanner'
import { ContentSeparator } from '../content/ContentSeparator/ContentSeparator.styled'
import { createRenderTarget } from '../render-target'
import { TextLink } from '../typography/TextLink/TextLink'
import { AuthenticationProviderContext } from '../user-registration/authentication/AuthenticationProvider'
import { ThemeMargins } from '../__styling/settings/metrics'
import {
    StyledAccountBox,
    StyledAccountText,
    StyledActionBox,
    StyledCommentIcon,
    StyledLinkButton,
    StyledRegisterBox,
    StyledRegisterText,
} from './Coral.styled'

export const LazyCoral = loadable(() => import('./Coral.web'))

const CoralRender = createRenderTarget('Coral', {
    web: LazyCoral,
    amp: null,
    rss: null,
    preview: null,
    app: LazyCoral,
})

export interface CoralProps {
    storyID?: string
    storyURL?: string
    articleCommentsEnabled?: boolean
    horizontalSpacing?: keyof ThemeMargins
    onEvent: (event: CommentEvent) => void
}

export function getCoralStoryId(
    section: string,
    publicHostname: string,
    articleId: string,
) {
    return section !== 'default'
        ? `${publicHostname}-${section}-${articleId}`
        : `${publicHostname}-${articleId}`
}

export const Coral: React.FC<CoralProps> = ({
    storyID,
    storyURL,
    articleCommentsEnabled,
    horizontalSpacing,
    onEvent,
}) => {
    const { coralUrl, publicUrl } = useContext(ConfigurationContext)

    const { subscriptionType, isLoggedIn } = useSelector(
        (state: AppState) => state.authentication,
    )
    const { section, kind } = useTheme()
    const areCommentsEnabled = useFeature('comments')

    // In The West, sections represent the regionals. In Perth Now, they represent the various topics (entertaintment/lifestyle/etc)
    // This will need to be changed once we get comments on the Regionals.
    const isCorrectSection =
        kind === Product.TheWest ? section === 'default' : true

    const canShowComments =
        areCommentsEnabled &&
        articleCommentsEnabled &&
        (kind === Product.TheWest || kind === Product.PerthNow) &&
        isCorrectSection
    subscriptionType !== 'corporate-site'

    const authContext = useContext(AuthenticationProviderContext)

    if (!canShowComments || typeof coralUrl === 'undefined') {
        return null
    }

    switch (kind) {
        case Product.PerthNow: {
            return (
                <React.Fragment>
                    <ContentSeparator
                        verticalBottomSpacing={'sm'}
                        verticalTopSpacing={'lg'}
                    />
                    {isLoggedIn ? (
                        <CoralRender
                            coralScriptUrl={coralUrl}
                            storyID={storyID}
                            onEvent={onEvent}
                            horizontalSpacing={horizontalSpacing}
                            hostName={publicUrl}
                            useContentSeperator={false}
                            storyURL={storyURL}
                        />
                    ) : (
                        <React.Fragment>
                            <StyledActionBox>
                                <StyledRegisterBox>
                                    <StyledCommentIcon />
                                    <StyledRegisterText>
                                        Register and have your say.
                                    </StyledRegisterText>
                                    <StyledLinkButton
                                        onClick={
                                            authContext &&
                                            authContext.onRegisterClick
                                        }
                                    >
                                        Register to comment
                                    </StyledLinkButton>
                                </StyledRegisterBox>
                                <StyledAccountBox>
                                    <StyledAccountText>
                                        Already have an account?
                                    </StyledAccountText>
                                    <StyledLinkButton
                                        onClick={
                                            authContext &&
                                            authContext.onLoginClick
                                        }
                                    >
                                        Log in
                                    </StyledLinkButton>
                                </StyledAccountBox>
                            </StyledActionBox>
                            <CoralRender
                                coralScriptUrl={coralUrl}
                                storyID={storyID}
                                onEvent={onEvent}
                                horizontalSpacing={horizontalSpacing}
                                hostName={publicUrl}
                                useContentSeperator={false}
                                storyURL={storyURL}
                            />
                        </React.Fragment>
                    )}
                </React.Fragment>
            )
        }
        default: {
            return (
                <EntitledToView
                    requiredAccess={{ level: 'subscriber' }}
                    entitled={() => (
                        <CoralRender
                            coralScriptUrl={coralUrl}
                            storyID={storyID}
                            onEvent={onEvent}
                            horizontalSpacing={horizontalSpacing}
                            hostName={publicUrl}
                            useContentSeperator={true}
                            storyURL={storyURL}
                        />
                    )}
                    notEntitled={() => (
                        <React.Fragment>
                            <MessageBanner
                                icon="comment"
                                id="coral_thread"
                                onEvent={onEvent}
                            >
                                <p>
                                    To comment on this story and join the
                                    conversation, subscribe to The West
                                    Australian’s{' '}
                                    <TextLink href="https://subscriber.thewest.com.au/subscribenow/digital?utm_source=TheWest&utm_medium=Article-Block&utm_campaign=Comments&utm_term=subscribe&utm_content=login">
                                        Everyday Digital package
                                    </TextLink>
                                    .
                                </p>
                            </MessageBanner>
                            <ContentSeparator verticalBottomSpacing="lg" />
                        </React.Fragment>
                    )}
                />
            )
        }
    }
}
